import type { DemoTemplate } from './types';

const PROF_MARTINEZ = 'prof.martinez@university.edu';
const ALEX = 'alex.thompson@university.edu';
const MAYA = 'maya.patel@university.edu';

const JORDAN = 'jordan.lee@university.edu';
const PRIYA = 'priya.sharma@university.edu';
const MARCUS = 'marcus.johnson@university.edu';
const EMMA = 'emma.chen@university.edu';
const TYLER = 'tyler.brooks@university.edu';
const SOFIA = 'sofia.rodriguez@university.edu';

export const educationTemplate: DemoTemplate = {
  id: 'education',
  name: 'Education',
  description: 'College classes, study groups, and academic meetings',
  selfAttendeeEmail: ALEX,

  attendees: [
    {
      email: PROF_MARTINEZ,
      name: 'Dr. Sarah Martinez',
      avatar: 'https://mockmind-api.uifaces.co/content/human/178.jpg',
    },
    {
      email: ALEX,
      name: 'Alex Thompson',
      avatar: 'https://mockmind-api.uifaces.co/content/human/195.jpg',
    },
    {
      email: MAYA,
      name: 'Maya Brown',
      avatar: 'https://mockmind-api.uifaces.co/content/human/156.jpg',
    },
    {
      email: JORDAN,
      name: 'Jordan Lee',
      avatar: 'https://mockmind-api.uifaces.co/content/human/142.jpg',
    },
    {
      email: PRIYA,
      name: 'Priya Sharma',
      avatar: 'https://mockmind-api.uifaces.co/content/human/189.jpg',
    },
    {
      email: MARCUS,
      name: 'Marcus Johnson',
      avatar: 'https://mockmind-api.uifaces.co/content/human/136.jpg',
    },
    {
      email: EMMA,
      name: 'Emma Chen',
      avatar: 'https://mockmind-api.uifaces.co/content/human/214.jpg',
    },
    {
      email: TYLER,
      name: 'Tyler Brooks',
      avatar: 'https://mockmind-api.uifaces.co/content/human/151.jpg',
    },
    {
      email: SOFIA,
      name: 'Sofia Rodriguez',
      avatar: 'https://mockmind-api.uifaces.co/content/human/164.jpg',
    },
  ],

  previousNotes: [
    {
      id: 'demo-prev-cs301-lecture',
      title: 'CS 301 Lecture: Graph Traversal Algorithms',
      forEventId: 'demo-cs-office-hours',
      daysAgo: 5,
      attendeeEmails: [],
      overview: 'Covered DFS and BFS algorithms with implementation details and complexity analysis.',
      briefSummary: 'DFS (stack-based) and BFS (queue-based) covered in depth. Problem Set 5 due next week covers cycle detection and shortest paths. Midterm will test tracing these algorithms by hand.',
      transcript: [
        { speakerEmail: 'Speaker', timestamp: '00:01', text: "Good afternoon everyone. Today we're covering graph traversal algorithms, specifically depth-first search and breadth-first search." },
        { speakerEmail: 'Speaker', timestamp: '00:18', text: "A graph is a data structure consisting of vertices and edges. Graphs can be directed or undirected, weighted or unweighted." },
        { speakerEmail: 'Speaker', timestamp: '00:35', text: "Graph traversal means visiting every vertex exactly once. DFS explores as far as possible along each branch before backtracking." },
        { speakerEmail: 'Speaker', timestamp: '00:52', text: "DFS uses a stack, either explicitly or through recursion. Mark current vertex as visited, then for each unvisited neighbor, recursively call DFS." },
        { speakerEmail: 'Speaker', timestamp: '01:12', text: "Time complexity is O(V+E). DFS is useful for detecting cycles, finding connected components, and topological sorting." },
        { speakerEmail: 'Speaker', timestamp: '01:32', text: "BFS explores all neighbors at current depth before moving deeper. It uses a queue. Mark vertices when you enqueue them, not when you dequeue." },
        { speakerEmail: 'Speaker', timestamp: '01:52', text: "BFS finds shortest paths in unweighted graphs. This shortest path property is crucial — DFS does not give you shortest paths." },
        { speakerEmail: 'Speaker', timestamp: '02:15', text: "Problem Set 5 is due next week. You'll implement both algorithms. One question asks you to detect cycles using DFS, another requires finding shortest paths using BFS." },
        { speakerEmail: 'Speaker', timestamp: '02:35', text: "These algorithms will be on the midterm. Practice on the exercises I posted. Next lecture we cover Dijkstra's algorithm." },
      ],
      socialFeedItems: [],
    },
    {
      id: 'demo-prev-cs-office-hours',
      title: 'CS 301 Office Hours',
      forEventId: 'demo-cs-office-hours',
      daysAgo: 7,
      attendeeEmails: [PROF_MARTINEZ],
      overview: 'Reviewed BST deletion edge cases and midterm preparation.',
      briefSummary: 'Worked through BST deletion with two children (in-order successor). AVL tree rotations flagged as key midterm topic. Research assistant position discussed.',
      actionItems: [
        { title: 'Complete Problem Set 4 BST implementation', assignedToEmail: ALEX, priority: 'High', daysOverdue: 1 },
        { title: 'Review AVL tree rotations for midterm', assignedToEmail: ALEX, priority: 'High' },
        { title: 'Apply for research assistant position', assignedToEmail: ALEX, priority: 'Medium' },
      ],
      transcript: [
        { speakerEmail: PROF_MARTINEZ, timestamp: '00:01', text: "Hi Alex, come on in. How did the intramural game go?" },
        { speakerEmail: ALEX, timestamp: '00:08', text: "We won! Made it to the finals next week actually." },
        { speakerEmail: PROF_MARTINEZ, timestamp: '00:15', text: "Congrats! Alright, what can I help you with today?" },
        { speakerEmail: ALEX, timestamp: '00:22', text: "I'm stuck on Problem Set 4, specifically the binary search tree deletion." },
        { speakerEmail: PROF_MARTINEZ, timestamp: '00:30', text: "The tricky part is handling the node with two children. What approach are you using?" },
        { speakerEmail: ALEX, timestamp: '00:42', text: "I'm trying to find the in-order successor, but my code breaks when the successor has a right child." },
        { speakerEmail: PROF_MARTINEZ, timestamp: '00:55', text: "That's a common edge case. You need to reconnect the successor's right child to its grandparent." },
        { speakerEmail: ALEX, timestamp: '01:10', text: "Oh, I see! Thanks. Are you still looking for research assistants?" },
        { speakerEmail: PROF_MARTINEZ, timestamp: '01:20', text: "Yes! We could use help with dataset preparation. Stop by after midterms if you're interested." },
      ],
      socialFeedItems: [
        { attendeeEmail: ALEX, text: 'Made the intramural basketball finals', confidence: 0.91, category: 'Sports', date: 'Dec 20' },
      ],
    },
    {
      id: 'demo-prev-capstone-meeting',
      title: 'Senior Capstone Team Meeting',
      forEventId: 'demo-capstone-meeting',
      daysAgo: 7,
      attendeeEmails: [MAYA],
      overview: 'Finalized database schema and split frontend work. Auth flow complete.',
      briefSummary: 'Alex completed user auth flow with JWT + Google OAuth. Maya starting API integration layer. Integration testing is the next sprint focus before demo day.',
      actionItems: [
        { title: 'Document API endpoints for Maya', assignedToEmail: ALEX, priority: 'High' },
        { title: 'Build API integration layer', assignedToEmail: MAYA, priority: 'High' },
        { title: 'Prepare demo day presentation slides', assignedToEmail: MAYA, priority: 'Medium' },
        { title: 'Set up integration testing framework', assignedToEmail: ALEX, priority: 'Medium' },
      ],
      transcript: [
        { speakerEmail: ALEX, timestamp: '00:01', text: "Hey Maya! Ready to wrap up before break?" },
        { speakerEmail: MAYA, timestamp: '00:06', text: "Yes! I'm flying home to Chicago tomorrow so want to get everything squared away." },
        { speakerEmail: ALEX, timestamp: '00:14', text: "Nice, have a good trip! Alright, let's talk about the database schema." },
        { speakerEmail: MAYA, timestamp: '00:25', text: "I think we should use PostgreSQL with a normalized schema for the user data." },
        { speakerEmail: ALEX, timestamp: '00:35', text: "Agreed. I finished the user authentication flow using JWT tokens. Works with Google OAuth too." },
        { speakerEmail: MAYA, timestamp: '00:45', text: "I'll start on the API integration layer this week. Should connect to your auth endpoints." },
        { speakerEmail: ALEX, timestamp: '00:55', text: "I'll document the API endpoints so you know the request/response formats." },
        { speakerEmail: MAYA, timestamp: '01:05', text: "For next sprint, we should focus on integration testing. Demo day is coming up fast." },
        { speakerEmail: ALEX, timestamp: '01:15', text: "True. We also need to start on the presentation slides. Want to divide those up?" },
        { speakerEmail: MAYA, timestamp: '01:22', text: "Sure, I can handle the technical architecture section. You do the demo walkthrough." },
      ],
      socialFeedItems: [
        { attendeeEmail: MAYA, text: 'Flying home to Chicago for the holidays', confidence: 0.87, category: 'Travel', date: 'Dec 21' },
      ],
    },
    {
      id: 'demo-prev-ai-club-meeting-1',
      title: 'AI Club Weekly Meeting',
      forEventId: 'demo-ai-club-meeting',
      daysAgo: 7,
      attendeeEmails: [JORDAN, PRIYA, MARCUS, EMMA, TYLER, SOFIA, MAYA],
      overview: 'Hackathon planning update. Budget approved, sponsor outreach assigned to Marcus.',
      briefSummary: 'Hackathon plans finalized, budget approved for workshop series. Marcus volunteered for sponsor outreach (3 companies interested). Mid-February target date. Workshop topics under discussion.',
      actionItems: [
        { title: 'Send sponsor outreach emails', assignedToEmail: MARCUS, priority: 'High', daysOverdue: 3 },
        { title: 'Book engineering building atrium for hackathon', assignedToEmail: PRIYA, priority: 'High' },
        { title: 'Prepare NLP workshop materials', assignedToEmail: SOFIA, priority: 'Medium' },
        { title: 'Draft neural networks workshop outline', assignedToEmail: EMMA, priority: 'Medium' },
        { title: 'Create ML deployment workshop slides', assignedToEmail: TYLER, priority: 'Medium' },
      ],
      transcript: [
        { speakerEmail: JORDAN, timestamp: '00:01', text: "Alright everyone, let's get started. Quick updates on hackathon planning." },
        { speakerEmail: PRIYA, timestamp: '00:12', text: "Hey Jordan! Congrats on getting into the summer ML research program!" },
        { speakerEmail: JORDAN, timestamp: '00:20', text: "Thanks Priya! We should chat about our hackathon project idea later." },
        { speakerEmail: EMMA, timestamp: '00:28', text: "Tyler and I were thinking of doing something with cloud deployment for ours." },
        { speakerEmail: TYLER, timestamp: '00:35', text: "Yeah, we've been studying for the AWS cert together. Sofia, you still doing the NLP thing?" },
        { speakerEmail: SOFIA, timestamp: '00:45', text: "Definitely! I just published my first NLP blog post. Got some good feedback." },
        { speakerEmail: MARCUS, timestamp: '00:55', text: "I've been talking to potential sponsors at my internship. Three companies interested." },
        { speakerEmail: JORDAN, timestamp: '01:05', text: "Perfect. Marcus, you'll send the sponsor outreach emails this week?" },
        { speakerEmail: MARCUS, timestamp: '01:12', text: "Got it. I'll send those out by Wednesday and compile responses for next meeting." },
        { speakerEmail: ALEX, timestamp: '01:20', text: "Maya and I can help with logistics once our capstone wraps up." },
        { speakerEmail: MAYA, timestamp: '01:30', text: "Yeah, when are we thinking for the date?" },
        { speakerEmail: PRIYA, timestamp: '01:38', text: "Mid-February? After finals, before spring break." },
        { speakerEmail: EMMA, timestamp: '01:48', text: "Works for me. I can do a workshop on deploying ML models." },
        { speakerEmail: JORDAN, timestamp: '01:58', text: "Love it. Marcus on sponsor outreach, everyone think about workshops. Same time next week?" },
      ],
      socialFeedItems: [
        { attendeeEmail: JORDAN, text: 'Finally beat that boss in Elden Ring', confidence: 0.85, category: 'Gaming', date: 'Dec 19' },
        { attendeeEmail: PRIYA, text: 'Getting into rock climbing at the campus gym', confidence: 0.86, category: 'Fitness', date: 'Dec 20' },
        { attendeeEmail: SOFIA, text: 'Getting into pottery at the arts center', confidence: 0.86, category: 'Hobbies', date: 'Dec 19' },
        { attendeeEmail: EMMA, text: 'Just adopted a cat from the shelter', confidence: 0.93, category: 'Personal', date: 'Dec 21' },
        { attendeeEmail: TYLER, text: 'Finally finished reading Dune', confidence: 0.85, category: 'Books', date: 'Dec 18' },
      ],
    },
    {
      id: 'demo-prev-ai-club-meeting-2',
      title: 'AI Club Weekly Meeting',
      forEventId: 'demo-ai-club-meeting',
      daysAgo: 14,
      attendeeEmails: [JORDAN, PRIYA, MARCUS, EMMA, TYLER, SOFIA],
      overview: 'Initial hackathon planning. Venue options discussed, sponsorship strategy outlined.',
      briefSummary: 'Hackathon venue: engineering building atrium (Priya to book). Marcus drafting sponsorship packet with tier levels. Workshop topics brainstormed: neural networks, ML deployment, NLP basics.',
      actionItems: [
        { title: 'Draft sponsorship packet with tier levels', assignedToEmail: MARCUS, priority: 'High', daysOverdue: 5 },
        { title: 'Share hackathon template from last year', assignedToEmail: ALEX, priority: 'Low' },
        { title: 'Get workshop budget approval from department', assignedToEmail: JORDAN, status: 'Done' },
      ],
      transcript: [
        { speakerEmail: JORDAN, timestamp: '00:01', text: "Welcome back everyone! Let's dive into hackathon planning." },
        { speakerEmail: PRIYA, timestamp: '00:10', text: "I talked to student activities. We can use the engineering building atrium." },
        { speakerEmail: JORDAN, timestamp: '00:20', text: "That's perfect. Emma, how'd the coding competition go?" },
        { speakerEmail: EMMA, timestamp: '00:28', text: "Third place! Not bad for my first regional competition." },
        { speakerEmail: TYLER, timestamp: '00:38', text: "Sofia and I started the Kaggle NLP challenge. Lots of preprocessing but making progress." },
        { speakerEmail: MARCUS, timestamp: '00:48', text: "On sponsorship: my startup has connections. I can reach out to their network." },
        { speakerEmail: JORDAN, timestamp: '00:58', text: "That would be huge. Can you put together a list of contacts?" },
        { speakerEmail: MARCUS, timestamp: '01:08', text: "Sure. I'll draft a sponsorship packet too. Gold, silver, bronze tiers." },
        { speakerEmail: ALEX, timestamp: '01:18', text: "Maya and I did something similar last year. We can share our template." },
        { speakerEmail: EMMA, timestamp: '01:28', text: "I've been thinking about doing an intro to neural networks workshop." },
        { speakerEmail: TYLER, timestamp: '01:38', text: "I could do one on ML environments — Docker, GPU setup, infrastructure stuff." },
        { speakerEmail: SOFIA, timestamp: '01:48', text: "I'd love to do NLP basics. Text preprocessing and sentiment analysis." },
        { speakerEmail: JORDAN, timestamp: '01:58', text: "Let's vote on final three next week. Great progress everyone!" },
      ],
      socialFeedItems: [
        { attendeeEmail: EMMA, text: 'Really into this new Taylor Swift album', confidence: 0.84, category: 'Music', date: 'Dec 15' },
        { attendeeEmail: MARCUS, text: 'Started learning guitar over winter break', confidence: 0.90, category: 'Hobbies', date: 'Dec 16' },
        { attendeeEmail: PRIYA, text: 'Binge-watching Succession over break', confidence: 0.89, category: 'Entertainment', date: 'Dec 17' },
      ],
    },
    {
      id: 'demo-prev-study-group',
      title: 'Algorithms Study Group',
      forEventId: 'demo-cs-office-hours',
      daysAgo: 3,
      attendeeEmails: [EMMA, PRIYA, TYLER],
      overview: 'Reviewed graph algorithms and practiced midterm problems together.',
      briefSummary: 'Group worked through DFS cycle detection and BFS shortest path problems. Priya found a tricky edge case in topological sort. Planning another session before midterm.',
      actionItems: [
        { title: 'Practice 5 more graph traversal problems', assignedToEmail: ALEX, priority: 'High' },
        { title: 'Write up topological sort edge case notes', assignedToEmail: PRIYA, priority: 'Medium' },
        { title: 'Schedule pre-midterm study session', assignedToEmail: EMMA, priority: 'Medium' },
      ],
      transcript: [
        { speakerEmail: ALEX, timestamp: '00:01', text: "Thanks for coming everyone. The midterm is next week so let's grind through these graph problems." },
        { speakerEmail: EMMA, timestamp: '00:08', text: "I've been struggling with the cycle detection using DFS. Can we start there?" },
        { speakerEmail: PRIYA, timestamp: '00:15', text: "Sure! The key is tracking the recursion stack separately from visited nodes." },
        { speakerEmail: TYLER, timestamp: '00:25', text: "Right, a back edge to a node in the current recursion stack means there's a cycle." },
        { speakerEmail: ALEX, timestamp: '00:35', text: "Let me trace through this example graph on the whiteboard." },
        { speakerEmail: PRIYA, timestamp: '00:45', text: "Wait, I found an edge case. What about topological sort when there are multiple valid orderings?" },
        { speakerEmail: EMMA, timestamp: '00:55', text: "Good catch. The answer depends on which neighbor you visit first." },
        { speakerEmail: TYLER, timestamp: '01:05', text: "We should do another session before the midterm. Maybe this weekend?" },
      ],
      socialFeedItems: [
        { attendeeEmail: TYLER, text: 'Study group with Emma for cloud computing final', confidence: 0.85, category: 'Academic', date: 'Dec 22' },
      ],
    },
  ],
};
